import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/hooks/useAuth";
import { PetProvider } from "@/hooks/usePet";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { PageTransition } from "@/components/animations/PageTransition";
import React, { Suspense, useState, useCallback } from "react";
import { SplashScreen } from "@/components/SplashScreen";
import ProtectedRoute from "@/components/ProtectedRoute";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { GlobalNavbar, GLOBAL_NAV_HEIGHT } from "@/components/GlobalNavbar";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useScrollRestoration } from "@/hooks/useScrollRestoration";
import { LocaleProvider } from "@/i18n/LocaleProvider";
import { ChatHeaderProvider } from "@/hooks/useChatHeaderContext";
import { useStructuredData } from "@/hooks/useStructuredData";
import { buildOrganizationSchema, buildWebSiteSchema } from "@/lib/structuredData";
import { safeGetItem, safeSetItem } from "@/lib/storage";

// Eager load critical path (landing = first paint)
import Landing from "./pages/Landing";

// Lazy load everything else
const AuthPage = React.lazy(() => import("./pages/Auth"));
const Onboarding = React.lazy(() => import("./pages/Onboarding"));
const Chat = React.lazy(() => import("./pages/Chat"));
const Settings = React.lazy(() => import("./pages/Settings"));
const PetProfile = React.lazy(() => import("./pages/PetProfile"));
const KidsReport = React.lazy(() => import("./pages/KidsReport"));
const TrialFinale = React.lazy(() => import("./pages/TrialFinale"));
const Garden = React.lazy(() => import("./pages/Garden"));
const BreedResults = React.lazy(() => import("./pages/BreedResults"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Journal = React.lazy(() => import("./pages/Journal"));
const Pricing = React.lazy(() => import("./pages/Pricing"));
const AIPetCompanion = React.lazy(() => import("./pages/AIPetCompanion"));
const PetMemorial = React.lazy(() => import("./pages/PetMemorial"));
const About = React.lazy(() => import("./pages/About"));
const Blog = React.lazy(() => import("./pages/Blog"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const WhatIsPeternity = React.lazy(() => import("./pages/WhatIsPeternity"));
const FAQHub = React.lazy(() => import("./pages/FAQHub"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

const LazyFallback = () => (
  <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-chat-bg gap-3">
    <span className="text-4xl animate-pulse">🐾</span>
    <span className="text-xs font-bold tracking-[3px] text-foreground/25 uppercase">Peternity</span>
  </div>
);

/** Helper for legacy redirects with :param */
const RedirectWithParam: React.FC<{ to: string }> = ({ to }) => {
  const params = useParams();
  const id = Object.values(params)[0];
  return <Navigate to={`${to}/${id}`} replace />;
};

/** Shared routes used inside each locale context */
const AppRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<LazyFallback />}>
        <Routes location={location} key={location.pathname}>
          {/* PUBLIC routes - no auth required */}
          <Route index element={<PageTransition><Landing /></PageTransition>} />
          <Route path="auth" element={<PageTransition><AuthPage /></PageTransition>} />
          <Route path="pricing" element={<PageTransition><Pricing /></PageTransition>} />
          <Route path="ai-pet-companion" element={<PageTransition><AIPetCompanion /></PageTransition>} />
          <Route path="ai-kompanion-pitomec" element={<PageTransition><AIPetCompanion /></PageTransition>} />
          <Route path="companero-mascota-ia" element={<PageTransition><AIPetCompanion /></PageTransition>} />
          <Route path="pet-memorial" element={<PageTransition><PetMemorial /></PageTransition>} />
          <Route path="memorial-pitomca" element={<PageTransition><PetMemorial /></PageTransition>} />
          <Route path="memorial-mascota" element={<PageTransition><PetMemorial /></PageTransition>} />
          <Route path="about" element={<PageTransition><About /></PageTransition>} />
          <Route path="o-nas" element={<PageTransition><About /></PageTransition>} />
          <Route path="sobre-nosotros" element={<PageTransition><About /></PageTransition>} />
          <Route path="blog" element={<PageTransition><Blog /></PageTransition>} />
          <Route path="blog/:slug" element={<PageTransition><BlogPost /></PageTransition>} />
          <Route path="what-is-peternity" element={<PageTransition><WhatIsPeternity /></PageTransition>} />
          <Route path="chto-takoe-peternity" element={<PageTransition><WhatIsPeternity /></PageTransition>} />
          <Route path="que-es-peternity" element={<PageTransition><WhatIsPeternity /></PageTransition>} />
          <Route path="faq" element={<PageTransition><FAQHub /></PageTransition>} />
          <Route path="voprosy" element={<PageTransition><FAQHub /></PageTransition>} />
          <Route path="preguntas-frecuentes" element={<PageTransition><FAQHub /></PageTransition>} />
          <Route path="ceny" element={<PageTransition><Pricing /></PageTransition>} />
          <Route path="precios" element={<PageTransition><Pricing /></PageTransition>} />
          <Route path="garden" element={<Garden />} />

          {/* PRIVATE routes - all under /app/ with single ProtectedRoute */}
          <Route path="app/*" element={<ProtectedRoute />}>
            <Route path="chat" element={<Chat />} />
            <Route path="settings" element={<PageTransition><Settings /></PageTransition>} />
            <Route path="onboarding" element={<Onboarding />} />
            <Route path="journal" element={<Journal />} />
            <Route path="pet/:petId" element={<PetProfile />} />
            <Route path="report/:petId" element={<KidsReport />} />
            <Route path="finale/:petId" element={<TrialFinale />} />
            <Route path="breeds/:petId" element={<BreedResults />} />
            <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
          </Route>

          {/* Legacy redirects for old URLs (existing users) */}
          <Route path="chat" element={<Navigate to="app/chat" replace />} />
          <Route path="settings" element={<Navigate to="app/settings" replace />} />
          <Route path="onboarding" element={<Navigate to="app/onboarding" replace />} />
          <Route path="journal" element={<Navigate to="app/journal" replace />} />
          <Route path="pet/:petId" element={<RedirectWithParam to="app/pet" />} />
          <Route path="report/:petId" element={<RedirectWithParam to="app/report" />} />
          <Route path="finale/:petId" element={<RedirectWithParam to="app/finale" />} />
          <Route path="breeds/:petId" element={<RedirectWithParam to="app/breeds" />} />

          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

/** Auto-detects locale from localStorage or browser, redirects once */
const LocaleAutoDetect: React.FC = () => {
  const location = useLocation();
  const saved = safeGetItem("preferred_locale");
  const browserLang = (navigator.language || "en").toLowerCase().slice(0, 2);
  const lang = saved || browserLang;

  if (lang === "ru") {
    const to = "/ru" + (location.pathname === "/" ? "" : location.pathname) + location.search;
    return <Navigate to={to} replace />;
  }
  if (lang === "es") {
    const to = "/es" + (location.pathname === "/" ? "" : location.pathname) + location.search;
    return <Navigate to={to} replace />;
  }

  return (
    <LocaleProvider locale="en">
      <AppRoutes />
    </LocaleProvider>
  );
};

const AnimatedRoutes = () => {
  useKeyboardShortcuts();
  useScrollRestoration();
  useSessionTimeout();
  // Global JSON-LD: injected once, stable across all routes
  useStructuredData([buildOrganizationSchema(), buildWebSiteSchema()], 'global');

  return (
    <>
      <GlobalNavbar />
      <div style={{ paddingTop: GLOBAL_NAV_HEIGHT }}>
        <Routes>
          {/* Russian locale */}
          <Route path="/ru/*" element={
            <LocaleProvider locale="ru">
              <AppRoutes />
            </LocaleProvider>
          } />
          {/* Spanish locale */}
          <Route path="/es/*" element={
            <LocaleProvider locale="es">
              <AppRoutes />
            </LocaleProvider>
          } />
          {/* English (default) — no prefix, with auto-redirect based on browser/saved lang */}
          <Route path="/*" element={<LocaleAutoDetect />} />
        </Routes>
      </div>
      <MobileBottomNav />
    
    </>
  );
};

const App = () => {
  const [showSplash, setShowSplash] = useState(() => {
    if (safeGetItem("peternity_splash_shown")) return false;
    return true;
  });

  const handleSplashComplete = useCallback(() => {
    safeSetItem("peternity_splash_shown", "1");
    setShowSplash(false);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AnimatePresence mode="wait">
          {showSplash && <SplashScreen key="splash" onComplete={handleSplashComplete} />}
        </AnimatePresence>
        {!showSplash && (
          <AuthProvider>
            <PetProvider>
              <ChatHeaderProvider>
                <BrowserRouter>
                  <AnimatedRoutes />
                </BrowserRouter>
              </ChatHeaderProvider>
            </PetProvider>
          </AuthProvider>
        )}
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
